/** The tiler's metadata for the rasters on the map: bounds to fit, zoom range, warnings. */
import { useQueries } from '@tanstack/react-query';
import { fetchRasterMeta, type RasterMeta } from '@/api/viewer';
import type { LayerRef } from './layers';
import type { TokenSource } from './tokenSource';

export type { RasterMeta };

export interface RasterMetaState {
  metas: Record<string, RasterMeta>;
  errors: Record<string, unknown>;
  isLoading: boolean;
}

export function useRasterMeta(refs: LayerRef[], tokenSource: TokenSource): RasterMetaState {
  const rasters = refs.filter((r) => r.via === 'tiler');
  const results = useQueries({
    queries: rasters.map((ref) => ({
      queryKey: ['raster-meta', ref.object, ref.digest],
      queryFn: async () => fetchRasterMeta(tokenSource.baseUrl, ref.object, ref.digest, await tokenSource.getToken()),
      staleTime: Infinity,
      retry: false,
    })),
  });
  const metas: Record<string, RasterMeta> = {};
  const errors: Record<string, unknown> = {};
  results.forEach((r, i) => {
    const id = rasters[i]?.object;
    if (!id) return;
    if (r.data) metas[id] = r.data;
    if (r.error) errors[id] = r.error;
  });
  return { metas, errors, isLoading: results.some((r) => r.isLoading) };
}

/** The smallest box holding every raster, as `[w, s, e, n]`. */
export function unionBounds(metas: RasterMeta[]): [number, number, number, number] | null {
  const boxes = metas.map((m) => m.bounds).filter((b): b is [number, number, number, number] => Array.isArray(b) && b.length === 4);
  if (!boxes.length) return null;
  return [
    Math.min(...boxes.map((b) => b[0])),
    Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])),
    Math.max(...boxes.map((b) => b[3])),
  ];
}
